import { camelCase } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDebouncedState,
  useFrameCountQuery,
  useFrameListQuery,
  useSubmitAllFrameQuery,
} from '@/hook';
import {
  Close,
  Send,
  VButton,
  VFlex,
  VInput,
  VStack,
  type VStatusBadgeProps,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  useVModal,
} from '@/package';
import { VStatusBadge } from '@/package';
import { VTabContent } from '@/package';
import { useAuthStore } from '@/store';
import type { Task } from '@/type';
import { mapTaskStateToWorkProgress } from '@/util';

import FrameListPagination from '../TaskDetail/FrameListPagination';
import MyWorkFrameTable from './MyWorkFrameTable';
import MyWorkTaskStatistics from './MyWorkTaskStatistics';

const MyWorkTaskDetail = ({
  task,
  deselectTask,
}: {
  task: Task;
  deselectTask: () => void;
}) => {
  const { mutate: submit } = useSubmitAllFrameQuery();
  const { openConfirm } = useVModal();
  const {
    state: keyword,
    debouncedState: debouncedKeyword,
    onChange: onKeywordChange,
  } = useDebouncedState('');
  const { user } = useAuthStore();

  const { data: frameData } = useFrameListQuery({
    taskID: task.id,
    frameSeq: debouncedKeyword ? Number(debouncedKeyword) : null,
    userID: user.id,
  });

  const [tab, setTab] = useState<'list' | 'statistics'>('list');

  const { t } = useTranslation();

  const frameList = frameData?.frames ?? [];
  const { data: frameCount } = useFrameCountQuery({
    taskID: task.id,
    workerID: [user.id],
  });
  const { data: submitableCount } = useFrameCountQuery({
    taskID: task.id,
    workState: ['Working'],
    workerID: [user.id],
  });
  const isAllSubmitable = !!submitableCount;

  const taskState = mapTaskStateToWorkProgress(task);

  const onAllSubmitClick = () => {
    openConfirm({
      title: t('task.allSubmitAction.zyryan'),
      message: () => (
        <VStack gap={0.25}>
          <div className="text-body-xl text-black">
            Submit all ‘In Progress’ frames?
          </div>
          <div className="text-body-lg text-gray-600">
            Once submitted, you won’t be able to make any changes. <br />
            They will be sent to the admin for review.
          </div>
        </VStack>
      ),
      onConfirm: () => allSubmit(),
      confirmText: t('task.allSubmitAction.zyryan'),
    });
  };

  const allSubmit = () => {
    submit({
      pTaskID: task.id,
    });
  };

  return (
    <VStack className="h-full w-fit bg-white">
      <VStack className="w-full gap-4 px-8 py-[21px]">
        <VFlex items="center" justify="between">
          <div className="text-headline-sm text-gray-600">{task.id}</div>
          <Close
            role="button"
            onClick={deselectTask}
            className="h-6 w-6 text-gray-400"
          />
        </VFlex>

        <VStack className="gap-[9px]">
          <VFlex justify="between" items="center">
            <VStack className="px-2" gap={0.25}>
              {/* TODO: 내용 수정 필요 */}
              <div className="text-headline-sm font-semibold">
                {task.orgPath}
              </div>
              <div className="text-body-lg text-gray-400">{task.orgPath}</div>
            </VStack>

            <div className="h-fit">
              <VStatusBadge
                size="sm"
                variant={
                  camelCase(
                    taskState.toLowerCase(),
                  ) as VStatusBadgeProps['variant']
                }
              >
                {taskState}
              </VStatusBadge>
            </div>
          </VFlex>

          {/* {!isVP25SP6Enabled && <MyWorkTaskAnalytics task={task} />} */}
        </VStack>
      </VStack>

      <VTab
        defaultValue="list"
        value={tab}
        onValueChange={val => setTab(val as 'list' | 'statistics')}
        className="w-full flex-1 overflow-hidden"
      >
        <VFlex
          justify="between"
          className="w-full px-8 py-2 shadow-sm"
          items="center"
        >
          <VFlex gap={1} items="center">
            <VTabTriggerContainer>
              <VTabTrigger value="list"> List </VTabTrigger>
              <VTabTrigger value="statistics"> Statistics </VTabTrigger>
            </VTabTriggerContainer>

            {tab === 'list' && (
              <div className="w-[264px]">
                <VInput
                  size="lg"
                  type="search"
                  fluid
                  value={keyword}
                  placeholder={t('frame.frameSeqSearchPlaceholder.crenic')}
                  onChange={e => onKeywordChange(e.target.value)}
                />
              </div>
            )}
          </VFlex>

          {tab === 'list' && (
            <VButton
              disabled={!isAllSubmitable}
              size="lg"
              type="button"
              onClick={onAllSubmitClick}
            >
              <Send className="h-5 w-5" />
              {t('task.allSubmitAction.zyryan')} ({submitableCount ?? 0})
            </VButton>
          )}
        </VFlex>

        <VStack className="w-full flex-1 overflow-y-hidden">
          <div className="w-[1036px] flex-1 overflow-x-hidden overflow-y-auto px-8 py-2">
            <VTabContent value="list">
              <VStack gap={0.5} className="w-full">
                <VFlex className="px-2 text-body-xl text-gray-500" gap={0.5}>
                  <span>Total</span>
                  <span>{frameCount ?? 0}</span>
                </VFlex>
                <MyWorkFrameTable frameList={frameList} task={task} />
              </VStack>
            </VTabContent>

            <VTabContent value="statistics">
              <MyWorkTaskStatistics taskDetail={task} />
            </VTabContent>
          </div>

          <VTabContent value="list">
            <div className="w-full pb-4">
              <FrameListPagination count={frameCount ?? 0} />
            </div>
          </VTabContent>
        </VStack>
      </VTab>
    </VStack>
  );
};

export default MyWorkTaskDetail;
